import { useState, useRef, useEffect } from "react";
import { Compass, Send, Paperclip, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCompassChat } from "@/components/chat/useCompassChat";
import { ChatHeader, StateSelect, ModeTabs, MessageList, AttachmentChips } from "@/components/chat/ChatParts";

const Composer = ({ mode, streaming, onSend, chat, user, onRequireAuth }) => {
  const [input, setInput] = useState("");
  const fileRef = useRef(null);
  const canAttach = mode === "chat";
  const hasFiles = chat.attachments.length > 0;
  const submit = (e) => { e.preventDefault(); if (!input.trim() && !(canAttach && hasFiles)) return; onSend(input); setInput(""); };
  const pick = () => {
    if (!user) { toast.info("Sign in to attach a screenshot or document."); onRequireAuth?.(); return; }
    fileRef.current?.click();
  };
  const onFiles = (e) => { Array.from(e.target.files || []).forEach(chat.addAttachment); e.target.value = ""; };
  return (
    <div className="border-t border-[#223552] p-3 bg-[#111B2C]">
      {canAttach && <AttachmentChips attachments={chat.attachments} uploading={chat.uploading} onRemove={chat.removeAttachment} />}
      <form onSubmit={submit} className="flex items-end gap-2">
        {canAttach && (
          <>
            <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf" onChange={onFiles} className="hidden" data-testid="chat-file-input" />
            <button type="button" onClick={pick} disabled={streaming || chat.uploading} title="Attach a screenshot or court letter" data-testid="chat-attach"
              className="h-10 w-10 shrink-0 rounded-xl border border-[#223552] bg-[#0B111E] hover:border-amber-400/60 disabled:opacity-40 flex items-center justify-center text-slate-300 hover:text-amber-300 transition-colors">
              {chat.uploading ? <Loader2 className="h-4 w-4 animate-spin text-amber-400" /> : <Paperclip className="h-4 w-4" />}
            </button>
          </>
        )}
        <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={1} data-testid="chat-input"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) submit(e); }}
          placeholder={mode === "research" ? "Ask a state-specific legal question..." : hasFiles ? "Ask about this file, or just send" : "Type here, or attach a court letter…"}
          className="flex-1 resize-none bg-[#0B111E] border border-[#223552] rounded-xl text-sm text-white px-3 py-2.5 outline-none focus:border-amber-400 max-h-28" />
        <button type="submit" disabled={streaming || chat.uploading || (!input.trim() && !(canAttach && hasFiles))} data-testid="chat-send"
          className="h-10 w-10 shrink-0 rounded-xl bg-[#E5A93C] hover:bg-[#F5BA4E] disabled:opacity-40 flex items-center justify-center transition-colors">
          <Send className="h-4 w-4 text-[#0B111E]" />
        </button>
      </form>
    </div>
  );
};

export const AIChatWidget = ({ onRequireAuth }) => {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const chat = useCompassChat(open);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [chat.messages, open]);

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="chat-launcher" aria-label="Open Father's Compass AI guide"
        style={{ bottom: "max(1.25rem, env(safe-area-inset-bottom))" }} className={`fixed right-5 z-40 h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-gradient-to-br from-[#E5A93C] to-[#C86A36] shadow-2xl flex items-center justify-center transition-transform hover:scale-105 ${open ? "hidden" : ""}`}>
        <Compass className="h-7 w-7 text-[#0B111E]" strokeWidth={2.2} />
        <span className="absolute -top-1 -right-1 h-4 w-4 bg-crimson rounded-full aff-pulse border-2 border-[#0B111E]" />
      </button>

      {open && (
        <div className="fixed bottom-0 right-0 sm:bottom-5 sm:right-5 z-50 w-full sm:w-[400px] h-[100dvh] sm:h-[620px] sm:max-h-[85vh] flex flex-col bg-[#0B111E] sm:rounded-2xl border border-[#223552] shadow-2xl overflow-hidden aff-fade-up" data-testid="chat-panel">
          <ChatHeader onClose={() => setOpen(false)} />
          <StateSelect value={chat.stateSel} onChange={chat.setStateSel} />
          <ModeTabs mode={chat.mode} setMode={chat.setMode} stateSel={chat.stateSel} researchEnabled={chat.researchEnabled} />
          <MessageList messages={chat.messages} streaming={chat.streaming} onPrompt={chat.send} scrollRef={scrollRef} />
          <Composer mode={chat.mode} streaming={chat.streaming} onSend={chat.send} chat={chat} user={user} onRequireAuth={onRequireAuth} />
        </div>
      )}
    </>
  );
};
